import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

interface DashboardPageProps {
  currentUser: User | null;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ currentUser }) => {
  const [savedCalculations, setSavedCalculations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSavedCalculations = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, `users/${currentUser.uid}/savedCalculations`),
          orderBy('timestamp', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const calculations = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedCalculations(calculations);
      } catch (err: any) {
        console.error("Error fetching saved calculations:", err);
        setError("Failed to load saved calculations.");
      } finally {
        setLoading(false);
      }
    };

    fetchSavedCalculations();
  }, [currentUser]);

  if (!currentUser) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="info">Please log in to view your dashboard.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="text-center mb-4">Welcome, {currentUser.email}!</h2>
      <p className="text-center text-muted">Your personalized financial hub.</p>

      <hr className="my-4" />

      <h4>Your Saved Calculations</h4>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : savedCalculations.length === 0 ? (
        <Alert variant="info">No saved calculations yet. Use the calculators and click 'Save Calculation'!</Alert>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {savedCalculations.map((calc) => (
            <Col key={calc.id}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>{calc.calculatorType.toUpperCase()} Calculation</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    Saved on: {new Date(calc.timestamp.toDate()).toLocaleString()}
                  </Card.Subtitle>
                  <Card.Text>
                    {/* Display key details based on calculator type */}
                    {
                      calc.calculatorType === 'emi' && (
                        <>
                          Principal: ₹{calc.principal}<br/>
                          Rate: {calc.rate}%<br/>
                          Tenure: {calc.tenure} years<br/>
                          Monthly EMI: ₹{calc.emi}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'gst' && (
                        <>
                          Amount: ₹{calc.amount}<br/>
                          Rate: {calc.rate}%<br/>
                          GST Amount: ₹{calc.gst}<br/>
                          Total: ₹{calc.totalAmount}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'profitMargin' && (
                        <>
                          Cost: ₹{calc.cost}<br/>
                          Revenue: ₹{calc.revenue}<br/>
                          Profit Margin: {calc.margin}%
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'incomeTax' && (
                        <>
                          Income: ₹{calc.income}<br/>
                          Age: {calc.age}<br/>
                          Regime: {calc.taxRegime}<br/>
                          Estimated Tax: ₹{calc.tax}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'sip' && (
                        <>
                          Monthly Investment: ₹{calc.monthlyInvestment}<br/>
                          Expected Return: {calc.expectedReturn}%<br/>
                          Time Period: {calc.timePeriod} years<br/>
                          Maturity Amount: ₹{calc.maturityAmount}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'capitalGains' && (
                        <>
                          Asset Type: {calc.assetType}<br/>
                          Purchase Price: ₹{calc.purchasePrice}<br/>
                          Sale Price: ₹{calc.salePrice}<br/>
                          Capital Gain: ₹{calc.result.capitalGain}<br/>
                          Estimated Tax: ₹{calc.result.taxAmount}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'nps' && (
                        <>
                          Monthly Investment: ₹{calc.monthlyInvestment}<br/>
                          Current Age: {calc.currentAge}<br/>
                          Expected Return: {calc.expectedReturn}%<br/>
                          Total Corpus: ₹{calc.result.totalCorpus}<br/>
                          Monthly Pension: ₹{calc.result.monthlyPension}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'hra' && (
                        <>
                          Basic Salary: ₹{calc.basicSalary}<br/>
                          HRA Received: ₹{calc.hraReceived}<br/>
                          Rent Paid: ₹{calc.rentPaid}<br/>
                          Exempted HRA: ₹{calc.exemptedHra}
                        </>
                      )
                    }
                    {
                      calc.calculatorType === 'ppf' && (
                        <>
                          Yearly Investment: ₹{calc.yearlyInvestment}<br/>
                          Time Period: {calc.timePeriod} years<br/>
                          Maturity Value: ₹{calc.result.maturityValue}
                        </>
                      )
                    }
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default DashboardPage;
